import { _js } from '@ifixit/utils/localize';
import { DropdownMenu, DropdownMenuTrigger } from '@ifixit/ui/components/dropdown-menu';
import { FaIcon } from '@ifixit/ui/components/icon';
import { Tooltip, TooltipContent, TooltipTrigger } from '@ifixit/ui/components/tooltip';
import { cn } from '@ifixit/ui/lib/utils';
import { memo } from 'react';
import {
   type RailDrag,
   type RailStepProps,
   StepMenuContent,
   stepAttention,
   stepThumb,
} from './rail_parts';

/**
 * Shown on hover or focus, always on touch. Touch gets a 24px button and no extra
 * hit area: two padded ones would cover a small tile and a tap would never select.
 */
const OVERLAY_BUTTON_CLASS =
   'absolute flex size-5 items-center justify-center rounded bg-black/60 text-white opacity-0 outline-none transition-opacity group-focus-within:opacity-100 group-hover:opacity-100 hover:bg-black/80 focus-visible:opacity-100 focus-visible:ring-[3px] focus-visible:ring-white/80 pointer-coarse:size-6 pointer-coarse:opacity-100';

/** One saved step as a numbered thumbnail: the list row's states, drag and menu, in a tile. */
export const GridTile = memo(function GridTile({
   step,
   displayIndex,
   isSelected,
   isMoved,
   isDragging,
   hasVideo,
   rowProps,
   handleProps,
   onSelect,
   onInsert,
   onDuplicate,
   historyUrlBase,
}: RailStepProps) {
   const thumb = stepThumb(step);
   const attention = stepAttention(step, hasVideo);
   const number = displayIndex + 1;
   // The number and dot are aria-hidden; this is the name they stand for.
   const label = [
      _js('Step %1', String(number)),
      step.title,
      isMoved ? _js('moved, not saved yet') : '',
      attention,
   ]
      .filter(part => part !== '')
      .join(', ');
   const select = (
      // data-drag: this button is the tile's body, so a press that turns into
      // a drag reorders (a plain click still selects the step).
      <button
         type="button"
         data-drag
         aria-label={label}
         aria-current={isSelected ? 'step' : undefined}
         className="absolute inset-0 cursor-grab overflow-hidden rounded-[5px] outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 active:cursor-grabbing"
         onClick={() => onSelect(step.stepid)}
      >
         {thumb ? (
            <img src={thumb} alt="" className="size-full object-cover" />
         ) : (
            <span className="flex size-full items-center justify-center text-gray-400">
               <FaIcon name="image" className="size-4" />
            </span>
         )}
         <NumberBadge number={number} />
         {attention !== '' && (
            <span
               aria-hidden
               className="absolute right-1 bottom-1 size-2.5 rounded-full bg-amber-600 ring-2 ring-white"
            />
         )}
      </button>
   );
   return (
      <li
         data-railstep={step.stepid}
         {...rowProps(displayIndex, { draggable: true })}
         className={cn(
            'group relative aspect-[4/3] min-w-0 cursor-grab rounded-md border bg-gray-50 active:cursor-grabbing',
            isSelected ? 'border-brand-500 ring-1 ring-brand-500' : 'border-gray-300',
            !isSelected && !isMoved && 'hover:border-gray-400',
            isMoved &&
               'not-has-[:focus-visible]:border-dashed not-has-[:focus-visible]:border-yellow-600',
            isDragging && 'opacity-60 ring-2 ring-brand-300'
         )}
      >
         {/* Touch has no hover or focus to open the tooltip, so the actions menu repeats the reason. */}
         {attention === '' ? (
            select
         ) : (
            <Tooltip>
               <TooltipTrigger asChild>{select}</TooltipTrigger>
               <TooltipContent>{attention}</TooltipContent>
            </Tooltip>
         )}
         <GridGrip
            displayIndex={displayIndex}
            handleProps={handleProps}
            label={_js('Reorder Step %1 (use the arrow keys to move it)', String(number))}
         />
         <DropdownMenu>
            <DropdownMenuTrigger
               aria-label={_js('Step %1 actions', String(number))}
               className={cn(
                  OVERLAY_BUTTON_CLASS,
                  'top-0.5 right-0.5 cursor-pointer data-[state=open]:opacity-100'
               )}
            >
               <FaIcon name="ellipsis-vertical" className="size-3" />
            </DropdownMenuTrigger>
            <StepMenuContent
               step={step}
               attention={attention}
               onInsert={onInsert}
               onDuplicate={onDuplicate}
               historyUrlBase={historyUrlBase}
            />
         </DropdownMenu>
      </li>
   );
});

/** The new-step editor's tile: it wears the insert position's number, like the list's draft row. */
export const DraftGridTile = memo(function DraftGridTile({
   displayIndex,
   isDragging,
   rowProps,
   handleProps,
}: {
   displayIndex: number;
   isDragging: boolean;
   rowProps: RailDrag['rowProps'];
   handleProps: RailDrag['handleProps'];
}) {
   return (
      <li
         data-railstep="new"
         aria-current="step"
         {...rowProps(displayIndex, { draggable: true })}
         className={cn(
            'group relative flex aspect-[4/3] min-w-0 cursor-grab items-center justify-center rounded-md border border-dashed border-brand-500 bg-white text-gray-400 ring-1 ring-brand-500 active:cursor-grabbing',
            isDragging && 'opacity-60 ring-brand-300'
         )}
      >
         <FaIcon name="plus" className="size-4" aria-hidden />
         <NumberBadge number={displayIndex + 1} />
         <span className="sr-only">
            {_js('Step %1', String(displayIndex + 1))}, {_js('New step')}
         </span>
         <GridGrip
            displayIndex={displayIndex}
            handleProps={handleProps}
            label={_js('Reorder the new step (use the arrow keys to move it)')}
         />
      </li>
   );
});

/** The keyboard and touch handle; a mouse can drag the whole tile. */
function GridGrip({
   displayIndex,
   handleProps,
   label,
}: {
   displayIndex: number;
   handleProps: RailDrag['handleProps'];
   label: string;
}) {
   return (
      <span
         {...handleProps(displayIndex, { label })}
         className={cn(OVERLAY_BUTTON_CLASS, 'top-0.5 left-0.5 cursor-grab active:cursor-grabbing')}
      >
         <FaIcon name="grip-vertical" className="size-3" aria-hidden />
      </span>
   );
}

function NumberBadge({ number }: { number: number }) {
   return (
      <span
         aria-hidden
         className="absolute bottom-0.5 left-0.5 min-w-4 rounded bg-white/90 px-1 text-center text-xs leading-4 font-medium text-gray-900 tabular-nums"
      >
         {number}
      </span>
   );
}
